import { useMemo, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { AnalyticsFilterBar } from '@/components/analytics/AnalyticsFilterBar';
import { WidgetCard } from '@/components/analytics/WidgetCard';
import { KpiWithDelta } from '@/components/analytics/KpiWithDelta';
import { ExportPdfTrigger } from '@/components/analytics/ExportPdfTrigger';
import { FixtureBadge } from '@/components/analytics/FixtureBadge';
import { Chart } from '@/components/charts/Chart';
import { SectionHeader } from '@/components/analytics/SectionHeader';
import { useOverviewRaData } from '@/hooks/useAnalyticsData';
import { useUIStore } from '@/stores/uiStore';
import type { SeriesDatum } from '@/components/charts/types';
import type { CategoryDatum } from '@/types/analytics';

// SE-side live data — reuse the same hooks the existing pages call. The
// hooks consume the existing /api/metrics/* endpoints; no new backend
// endpoint is introduced beyond the two new tier + services-by-name
// aggregators added for this Overview.
import {
  useFleetSummary,
  useDeploymentStats,
  useServiceSummary,
  useServicesByName,
  useProductTierBreakdown,
} from '@/hooks/useMetrics';
import { useServicePageSummary } from '@/hooks/useServices';

// Existing dashboard widgets reused inline.
import { DeploymentsChart } from '@/components/dashboard/DeploymentsChart';
import { ProfileBreakdown } from '@/components/dashboard/ProfileBreakdown';
import { VersionDistribution } from '@/components/dashboard/VersionDistribution';
import { HardwareDistribution } from '@/components/dashboard/HardwareDistribution';

/**
 * Analytics — Overview (MVP).
 *
 * GoodData "1 Overview" replacement. Mixed SE + RA data. Section layout
 * blends GoodData's topic structure (Deployments / Registered Users /
 * Endpoint Connections / Apps & Services) with explicit SE-vs-RA framing
 * so consumers can see where the data originates.
 */
export function Overview() {
  const { data, loading, error } = useOverviewRaData();
  const session = useUIStore((s) => s.session);

  // SE-side data hooks — same ones /, /fleet, /services already use.
  const { summary: fleet, loading: fleetLoading } = useFleetSummary();
  const { stats: deploymentStats, loading: deploymentLoading } = useDeploymentStats();
  const { summary: serviceSummary, loading: serviceLoading } = useServiceSummary();
  const { summary: servicePageSummary, loading: servicePageLoading } = useServicePageSummary();
  const { data: servicesByName, loading: servicesByNameLoading } = useServicesByName();
  const { data: productTier, loading: productTierLoading } = useProductTierBreakdown();

  // RA-side derived series.
  const endpointsByQuarter: SeriesDatum[] = useMemo(
    () =>
      (data?.endpointsByQuarter ?? []).map((q) => ({
        x: q.quarter,
        New: q.new,
        Cumulative: q.cumulative,
      })),
    [data]
  );
  const uniqueUsersSeries: SeriesDatum[] = useMemo(
    () =>
      (data?.uniqueActiveUsersSeries ?? []).map((p) => ({
        x: p.date,
        Daily: p.dailyUnique,
        '7-Day': p.sevenDayUnique,
        '30-Day': p.thirtyDayUnique,
      })),
    [data]
  );
  const endpointsActiveVsNever: CategoryDatum[] = useMemo(
    () => data?.endpointsActiveVsNeverUsed ?? [],
    [data]
  );
  const connectionsByType: CategoryDatum[] = useMemo(
    () => data?.connectionsByType ?? [],
    [data]
  );

  // Section 2 KPI derivations from the by-X breakdowns. These reproduce the
  // GoodData section's "Registered Users by Quarter / Domain / Type / Status"
  // widgets as compact KPI tiles since the breakdowns themselves don't add
  // enough signal to deserve a chart at the Overview level (the Active Users
  // page renders the full charts for anyone who wants the breakdown).
  const latestQuarterNew = useMemo(() => {
    const series = data?.registeredUsersByQuarter ?? [];
    return series.length ? series[series.length - 1].new : 0;
  }, [data]);
  const latestQuarterLabel = useMemo(() => {
    const series = data?.registeredUsersByQuarter ?? [];
    return series.length ? series[series.length - 1].quarter : '';
  }, [data]);
  const uniqueDomains = data?.registeredUsersByDomain.length ?? 0;
  const userTypeCount = data?.registeredUsersByType.length ?? 0;
  const activeStatusCount = useMemo(() => {
    const rows = data?.registeredUsersByStatus ?? [];
    const active = rows.find((r) => r.label.toLowerCase() === 'active');
    return active?.value ?? 0;
  }, [data]);

  // Services derived data.
  const servicesByNameData: CategoryDatum[] = useMemo(
    () => servicesByName.map((r) => ({ label: r.name, value: r.count })),
    [servicesByName]
  );
  const servicesByStatusData: CategoryDatum[] = useMemo(() => {
    if (!serviceSummary) return [];
    return [
      { label: 'HEALTHY', value: serviceSummary.healthy },
      { label: 'PENDING', value: serviceSummary.pending },
      { label: 'UNHEALTHY', value: serviceSummary.unhealthy },
      { label: 'TERMINATED', value: serviceSummary.terminated },
      { label: 'UNKNOWN', value: serviceSummary.unknown },
    ].filter((r) => r.value > 0);
  }, [serviceSummary]);

  // Total nodes running at least one service = distinct nodes in servicesPerNode.
  const nodesRunningServices = servicePageSummary?.servicesPerNode.length ?? 0;
  const nodesRunningServicesPct =
    fleet && fleet.totalNodes > 0 ? Math.round((nodesRunningServices / fleet.totalNodes) * 100) : 0;

  const pdfTargetRef = useRef<HTMLDivElement>(null);

  return (
    <div className="dls-space-y-4">
      <div className="dls-flex dls-items-center dls-justify-between">
        <div>
          <h1 className="dls-text-2xl dls-font-bold dls-text-foreground">Overview</h1>
          <p className="dls-text-sm dls-text-muted-foreground dls-mt-1">
            Fleet posture and customer activity at a glance. Mix of Secure Edge and Remote Access data.
          </p>
        </div>
        <div className="dls-flex dls-items-center dls-gap-2">
          <Badge variant="outline" className="dls-text-muted-foreground">SE + RA</Badge>
          <ExportPdfTrigger
            title={`Overview${session?.tenant?.name ? ` — ${session.tenant.name}` : ''}`}
            filenameSlug="overview"
            targetRef={pdfTargetRef}
          />
        </div>
      </div>

      <AnalyticsFilterBar />
      <p className="dls-text-xs dls-text-muted-foreground dls-px-1 dls-italic">
        Date range applies to Remote Access time-series widgets. Secure Edge sections render point-in-time fleet snapshots.
      </p>

      {error && (
        <div className="dls-rounded-md dls-border dls-border-status-offline/30 dls-bg-status-offline/10 dls-px-4 dls-py-3 dls-text-sm dls-text-status-offline">
          Failed to load RA half of overview — {error.message}. The SE widgets below should still render.
        </div>
      )}

      {/* PDF capture target. */}
      <div ref={pdfTargetRef} className="dls-space-y-4">

      {/* ── Section 1 — Secure Edge: Deployments + Edge Node Status ─────── */}
      <SectionHeader
        title="Secure Edge — Deployments + Edge Node Status"
        description={
          'Installation of physical Secure Edge nodes. "Unassigned" Nodes are those purchased but not yet ' +
          'deployed and assigned to a site. "New" nodes have been assigned to a site but have not yet been ' +
          'installed and connected to the WAN, so their current status of Alive or Unreachable (offline) ' +
          'cannot be determined.'
        }
      />

      {deploymentLoading ? (
        <ChartLoading height={320} />
      ) : deploymentStats ? (
        <DeploymentsChart stats={deploymentStats} />
      ) : (
        <ChartLoading height={120} />
      )}

      {/* Row 1: Edge Nodes by Status + Product Tier donuts */}
      <div className="dls-grid dls-grid-cols-1 lg:dls-grid-cols-2 dls-gap-4">
        <WidgetCard
          title="Edge Nodes by Status"
          info="ALIVE / UNASSIGNED / UNREACHABLE distribution. UNASSIGNED counts PKI serials purchased but not yet deployed to a node."
        >
          {fleetLoading || deploymentLoading || !fleet || !deploymentStats ? (
            <ChartLoading />
          ) : (
            <Chart
              type="donut"
              data={[
                { label: 'ALIVE', value: fleet.aliveNodes },
                { label: 'UNASSIGNED', value: deploymentStats.totalAvailable },
                { label: 'UNREACHABLE', value: fleet.unreachableNodes },
              ]}
              height={280}
              ariaLabel="Edge nodes by status"
            />
          )}
        </WidgetCard>

        <WidgetCard
          title="Deployments by Product Tier"
          info={
            "Product Tier (Basic / Pro / Enterprise) lives in HubSpot, so this is an approximation derived " +
            "from each node's hardware form factor + cluster membership. Basic = Advantech 2271 or Polywell J6412. " +
            "Enterprise = any node in a cluster of ≥2 members. Pro = everything else with recognized hardware. " +
            "Decision pending: keep as-is, swap to real HubSpot integration, or drop."
          }
        >
          {productTierLoading || !productTier ? (
            <ChartLoading />
          ) : (
            <Chart
              type="donut"
              data={[
                { label: 'Basic', value: productTier.Basic },
                { label: 'Pro', value: productTier.Pro },
                { label: 'Enterprise', value: productTier.Enterprise },
              ].filter((d) => d.value > 0)}
              height={280}
              ariaLabel="Deployments by product tier"
            />
          )}
        </WidgetCard>
      </div>

      {/* Row 2: Profile + Version + Hardware (kept as Neeve-specific deviations from GoodData) */}
      <div className="dls-grid dls-grid-cols-1 lg:dls-grid-cols-3 dls-gap-4">
        <WidgetCard title="Profile Breakdown" info="Hardware deployment type: Edge / Cloud Connector / Virtual Edge / Unified Cloud Gateway." hideMenu>
          {fleetLoading || !fleet ? (
            <ChartLoading />
          ) : (
            <ProfileBreakdown breakdown={fleet.profileBreakdown ?? {}} />
          )}
        </WidgetCard>
        <WidgetCard title="Version Distribution" info="Software-version distribution across the fleet." hideMenu>
          {fleetLoading || !fleet ? (
            <ChartLoading />
          ) : (
            <VersionDistribution
              versionDistribution={fleet.versionDistribution ?? {}}
              latestVersion={fleet.versionCompliance?.latestVersion ?? null}
            />
          )}
        </WidgetCard>
        <WidgetCard title="Hardware Distribution" info="Vendor / model distribution across the fleet." hideMenu>
          {fleetLoading || !fleet ? (
            <ChartLoading />
          ) : (
            <HardwareDistribution hardwareVendors={fleet.hardwareVendors ?? {}} />
          )}
        </WidgetCard>
      </div>

      {/* ── Section 2 — Remote Access: Registered Users ─────────────────── */}
      <SectionHeader
        title="Remote Access — Registered Users"
        description={
          'Registered users have been created in the system but may have never logged in. Active users have ' +
          'logged in and used Remote Access features. "Daily unique users" are users that have logged in each day; ' +
          '"7-day unique users" are total unique users logged in over the last 7 days; "30-day unique users" are ' +
          'total unique users logged in over the last 30 days. For more details visit the Active Users page.'
        }
        trailing={<FixtureBadge />}
      />

      {/* KPI row — the GoodData section had "Registered Users by Quarter /
          Domain / Type / Status" as widgets; they don't add signal as charts at
          the Overview level, so we surface them here as KPI counts. The Active
          Users page renders the full breakdowns for anyone who wants them. */}
      <div className="dls-grid dls-grid-cols-2 md:dls-grid-cols-3 lg:dls-grid-cols-6 dls-gap-3">
        <WidgetCard title="Registered Users" hideMenu>
          <KpiWithDelta value={loading ? '—' : data?.registeredUsers ?? 0} subtitle="Total Registered" />
        </WidgetCard>
        <WidgetCard title="Active Users (Last 30 Days)" hideMenu>
          <KpiWithDelta
            value={loading ? '—' : data?.activeUsersLast30Days.current ?? 0}
            previousValue={loading ? undefined : data?.activeUsersLast30Days.previousPeriod}
            deltaMode="percent"
            goodWhen="up"
            subtitle="vs Last Year"
          />
        </WidgetCard>
        <WidgetCard title="Registered Users by Quarter" hideMenu>
          <KpiWithDelta
            value={loading ? '—' : latestQuarterNew}
            subtitle={loading ? undefined : `New in ${latestQuarterLabel}`}
          />
        </WidgetCard>
        <WidgetCard title="Registered Users by Domain" hideMenu>
          <KpiWithDelta value={loading ? '—' : uniqueDomains} subtitle="Unique Domains" />
        </WidgetCard>
        <WidgetCard title="Registered Users by Type" hideMenu>
          <KpiWithDelta value={loading ? '—' : userTypeCount} subtitle="User Types" />
        </WidgetCard>
        <WidgetCard title="Registered Users by Status" hideMenu>
          <KpiWithDelta value={loading ? '—' : activeStatusCount} subtitle="Active" />
        </WidgetCard>
      </div>

      <WidgetCard title="Unique Active Users" info="Daily / 7-day / 30-day rolling unique active users.">
        {loading ? (
          <ChartLoading />
        ) : (
          <Chart
            type="line"
            data={uniqueUsersSeries}
            xKey="x"
            seriesKeys={['Daily', '7-Day', '30-Day']}
            height={280}
            ariaLabel="Unique active users — daily, 7-day, 30-day"
          />
        )}
      </WidgetCard>

      {/* ── Section 3 — Remote Access: Endpoint Connections ─────────────── */}
      <SectionHeader
        title="Remote Access — Endpoint Connections"
        description={
          'Endpoints are connections to remote devices configured in Remote Access. "Active Endpoints" have ' +
          'been used at least once; those that are "Inactive" have never had a remote access session. For more ' +
          'information visit the Active Endpoints page.'
        }
        trailing={<FixtureBadge />}
      />

      {/* Active Endpoints KPI lives here (in Endpoint Connections), not in
          Section 2 where Registered Users lives. */}
      <div className="dls-grid dls-grid-cols-1 md:dls-grid-cols-3 dls-gap-3">
        <WidgetCard title="Active Endpoints" hideMenu>
          <KpiWithDelta
            value={loading ? '—' : data?.activeEndpoints.active ?? 0}
            unit={loading ? undefined : ` of ${data?.activeEndpoints.totalOnboarded ?? 0}`}
            subtitle={loading ? undefined : `${data?.activeEndpoints.percentActive ?? 0}% onboarded`}
          />
        </WidgetCard>
      </div>

      <div className="dls-grid dls-grid-cols-1 lg:dls-grid-cols-2 dls-gap-4">
        <WidgetCard title="Endpoints — Active vs Never Used" info="Onboarded endpoints split by activity status.">
          {loading ? (
            <ChartLoading />
          ) : (
            <Chart type="donut" data={endpointsActiveVsNever} height={280} ariaLabel="Endpoints active vs never used" />
          )}
        </WidgetCard>
        <WidgetCard title="Connections by Type" info="Protocol distribution: RDP / HTTP / SSH / HTTPS / VNC / TELNET / TCP.">
          {loading ? (
            <ChartLoading />
          ) : (
            <Chart type="donut" data={connectionsByType} height={280} ariaLabel="Connections by type" />
          )}
        </WidgetCard>
      </div>

      <WidgetCard
        title="Total Endpoints by Quarter"
        info="New endpoints onboarded per quarter (bars) overlaid with the running cumulative total (line)."
      >
        {loading ? (
          <ChartLoading />
        ) : (
          <Chart
            type="combo"
            data={endpointsByQuarter}
            xKey="x"
            barKey="New"
            lineKey="Cumulative"
            barLabel="New per quarter"
            lineLabel="Cumulative total"
            dualAxis
            height={280}
            ariaLabel="Endpoints by quarter — new (bars) and cumulative (line)"
          />
        )}
      </WidgetCard>

      {/* ── Section 4 — Secure Edge: Apps & Services ────────────────────── */}
      <SectionHeader
        title="Secure Edge — Apps & Services"
        description={
          'Services are containerized applications running on the edge. These may be simple utilities such ' +
          'as "estrace" or 3rd-party applications from the marketplace such as Auvik or Iceberg.'
        }
      />

      {/* Layout mirrors Section 3 (Endpoint Connections): single KPI on top,
          two donuts side-by-side below. */}
      <div className="dls-grid dls-grid-cols-1 md:dls-grid-cols-3 dls-gap-3">
        <WidgetCard title="Nodes Running Services" hideMenu>
          <KpiWithDelta
            value={servicePageLoading || fleetLoading ? '—' : nodesRunningServices}
            unit={
              servicePageLoading || fleetLoading
                ? undefined
                : ` of ${fleet?.totalNodes ?? 0}`
            }
            subtitle={
              servicePageLoading || fleetLoading
                ? undefined
                : `${nodesRunningServicesPct}% of Total Deployments`
            }
          />
        </WidgetCard>
      </div>

      <div className="dls-grid dls-grid-cols-1 lg:dls-grid-cols-2 dls-gap-4">
        <WidgetCard title="Services by Name (per Node)" info="Distribution of services across the fleet by service name. Top 25 by count.">
          {servicesByNameLoading ? (
            <ChartLoading />
          ) : (
            <Chart type="donut" data={servicesByNameData} height={280} ariaLabel="Services by name" />
          )}
        </WidgetCard>
        <WidgetCard title="Services by Status (per Node)" info="HEALTHY / PENDING / UNHEALTHY / TERMINATED / UNKNOWN distribution.">
          {serviceLoading || !serviceSummary ? (
            <ChartLoading />
          ) : (
            <Chart type="donut" data={servicesByStatusData} height={280} ariaLabel="Services by status" />
          )}
        </WidgetCard>
      </div>
      </div>
    </div>
  );
}

function ChartLoading({ height = 280 }: { height?: number }) {
  return (
    <div className="dls-flex dls-items-center dls-justify-center dls-text-sm dls-text-muted-foreground" style={{ height }}>
      Loading…
    </div>
  );
}
